import React, { useMemo } from 'react';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { formatDate, WidgetCard } from '../controller/shared.tsx';
import { useCaeTesterWorkload } from '../../../hooks/analytics/useCaeReports.ts';
import { BAR_RADIUS, CHART_AXIS_LINE, CHART_AXIS_TICK, CHART_GRID_STROKE, CHART_LEGEND_STYLE, CHART_TOOLTIP_STYLE, SEMANTIC } from '../theme.ts';

/**
 * Report #19 — Tester Workload Distribution. Table + bar chart of assigned
 * vs. completed test runs per tester (assignee_user_id / due_date now exist
 * on test_runs post migration 000086 — this is the full version, not the
 * docx's gap placeholder).
 */
export const TesterWorkloadPanel: React.FC = () => {
  const { data, isLoading, isError, error, dataUpdatedAt, isFetching, refetch } = useCaeTesterWorkload();
  const rows = data ?? [];

  const chartData = useMemo(
    () => rows.map((r) => ({ tester: r.tester, assigned: r.assigned, completed: r.completed })),
    [data]
  );

  return (
    <WidgetCard
      title="Tester Workload Distribution"
      description="Assigned vs. completed test runs by tester, with next due date"
      loading={isLoading}
      error={isError ? error : null}
      errorMessage={error?.message ?? 'Failed to load tester workload.'}
      isEmpty={!isLoading && !isError && rows.length === 0}
      emptyMessage="No test runs are assigned to testers yet."
      updatedAt={dataUpdatedAt}
      onRefresh={refetch}
      isFetching={isFetching}
    >
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 10, right: 10, bottom: 10, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_STROKE} vertical={false} />
            <XAxis dataKey="tester" tick={CHART_AXIS_TICK} axisLine={CHART_AXIS_LINE} tickLine={false} />
            <YAxis tick={CHART_AXIS_TICK} axisLine={CHART_AXIS_LINE} tickLine={false} allowDecimals={false} />
            <Tooltip contentStyle={CHART_TOOLTIP_STYLE} cursor={{ fill: 'rgba(148, 163, 184, 0.08)' }} />
            <Legend wrapperStyle={CHART_LEGEND_STYLE} />
            <Bar dataKey="assigned" name="Assigned" fill={SEMANTIC.info.chart} radius={BAR_RADIUS} maxBarSize={28} />
            <Bar dataKey="completed" name="Completed" fill={SEMANTIC.good.chart} radius={BAR_RADIUS} maxBarSize={28} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="analytics-scroll mt-3 border-t border-slate-100 pt-3 max-h-64 overflow-y-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs font-medium uppercase tracking-wide text-slate-500">
              <th className="pb-2">Tester</th>
              <th className="pb-2 text-right">Assigned</th>
              <th className="pb-2 text-right">Completed</th>
              <th className="pb-2">Balance</th>
              <th className="pb-2">Next Due</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((row) => {
              const open = row.assigned - row.completed;
              return (
                <tr key={row.user_id} className="hover:bg-slate-50">
                  <td className="py-1.5 pr-2 text-slate-900">{row.tester}</td>
                  <td className="py-1.5 pr-2 text-right tabular-nums text-slate-600">{row.assigned}</td>
                  <td className="py-1.5 pr-2 text-right tabular-nums text-slate-600">{row.completed}</td>
                  <td className="py-1.5 pr-2">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${
                        open > 5 ? SEMANTIC.critical.chipClass : open > 0 ? SEMANTIC.warn.chipClass : SEMANTIC.good.chipClass
                      }`}
                    >
                      {open} open
                    </span>
                  </td>
                  <td className="py-1.5 text-slate-500">{formatDate(row.next_due)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </WidgetCard>
  );
};

export default TesterWorkloadPanel;
